import { InstrumentRunPublic } from '@/types';
import {
  FloatingPortal,
  autoUpdate,
  flip,
  arrow as floatingArrow,
  offset,
  safePolygon,
  shift,
  useFloating,
  useHover,
  useInteractions,
} from '@floating-ui/react';
import { useButton } from '@nautilusbio/nb-design-system/Button';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import Text from '@nautilusbio/nb-design-system/Text';
import { Link } from '@tanstack/react-router';
import React, { FC, memo, useMemo, useRef, useState } from 'react';

import styles from '../styles.module.css';

const arrowOffset = '-8px';
const InstrumentRunName: FC<{ data: InstrumentRunPublic }> = ({ data: { description, id, name, status } }) => {
  // Floating UI setup
  const [open, setOpen] = useState(false);
  const arrowRef = useRef<HTMLDivElement | null>(null);

  const { refs, floatingStyles, context, placement } = useFloating({
    middleware: [offset(8), flip(), shift(), floatingArrow({ element: arrowRef })],
    placement: 'top-start',
    whileElementsMounted: autoUpdate,
    open,
    onOpenChange: setOpen,
  });

  // Setup hover interaction with delay and safePolygon for better hover handling
  const hover = useHover(context, {
    delay: { open: 1000, close: 0 },
    enabled: !!description,
    handleClose: safePolygon(),
  });
  const interactions = useInteractions([hover]);

  // Arrow positioning (memoized for performance)
  const arrowStyle = useMemo(() => {
    const side = {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right',
    }[placement.split('-')[0]] as string;
    return { [side]: arrowOffset };
  }, [placement]);

  const { buttonClassName } = useButton({
    className: styles.runNameButton,
    fullWidth: true,
    fullHeight: true,
    size: 'xs',
    visual: 'unstyled',
  });

  // Accessibility: unique tooltip id for aria-describedby
  const tooltipId = description ? `instrument_run-${id}-desc-popover` : undefined;

  return (
    <>
      {status === 'draft' ? (
        <Link
          aria-describedby={tooltipId}
          className={buttonClassName}
          data-testid={`instrument_run-${id}-name-btn`}
          preload={false}
          ref={refs.setReference}
          search={{ mode: 'draft' as const, id: String(id) }}
          to="/instrument_runs/new"
          {...interactions.getReferenceProps()}
        >
          {name}
        </Link>
      ) : (
        <Link
          aria-describedby={tooltipId}
          className={buttonClassName}
          data-testid={`instrument_run-${id}-name-btn`}
          params={{ instrumentRunId: String(id) }}
          preload={false}
          ref={refs.setReference}
          to="/instrument_runs/$instrumentRunId"
          {...interactions.getReferenceProps()}
        >
          {name}
        </Link>
      )}

      {/* Mount the portal lazily only when the tooltip is open to avoid creating many mounted portals */}
      {description && open && (
        <FloatingPortal>
          <Stack
            className={styles.runNamePopover}
            gap={2}
            orientation="vertical"
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              opacity: open ? 1 : 0,
              pointerEvents: open ? 'auto' : 'none',
            }}
            {...interactions.getFloatingProps()}
            data-testid={`instrument_run-${id}-desc-popover`}
            id={tooltipId}
            role="tooltip"
          >
            {/* Arrow */}
            <div className={styles.runNamePopoverArrow} style={arrowStyle} data-placement={placement} ref={arrowRef} />

            <Text as="h5">{name}</Text>
            <Text as="p" size="sm">
              {description}
            </Text>
          </Stack>
        </FloatingPortal>
      )}
    </>
  );
};

InstrumentRunName.displayName = 'InstrumentRunName';

/**
 * Memo comparator:
 * Only re-render when the instrument run's id, name or description change.
 * This avoids unnecessary updates when parent components re-render with the same data object.
 */
const areEqual = (
  prevProps: Readonly<{ data: InstrumentRunPublic }>,
  nextProps: Readonly<{ data: InstrumentRunPublic }>,
) =>
  prevProps.data.id === nextProps.data.id &&
  prevProps.data.name === nextProps.data.name &&
  prevProps.data.description === nextProps.data.description;

export default memo(InstrumentRunName, areEqual);
